import type { FormEvent, ReactNode } from "react"
import { useMemo, useState } from "react"

import { clsx } from "clsx"

import { batteryConcertErrorCode as concertErrorCode } from "~/api/battery-concert-application-errors"
import type { ConcertSubmitApplicationCoApplicantDto } from "~/api/dto/concert.dto"
import { Drawer, TextField } from "~/components/ui-components/v2"

import { CONCERT_EYEBROW } from "./concert-application-copy"
import type {
  ConcertApplicationBundle,
  ConcertApplicationStatusView,
  ConcertSubmitApplicationBody,
} from "./concert-kind-bundle"
import { Eyebrow } from "../../../BatteryOnlyProposal/primitives"

export const CONCERT_APPLICATION_TITLE = `Concert application`
export const SUBMIT_APPLICATION_LABEL = `Submit application`
export const ADD_CO_APPLICANT_LABEL = `Add co-applicant`
export const REMOVE_CO_APPLICANT_LABEL = `Remove co-applicant`
export const DISCLOSURE_ACCEPT_LABEL = `I have read and agree to all disclosures, consents, and agreements above.`
export const OWNER_VERIFIED_LABEL = `Can you verify that you own the property address listed on the application in your name or directly through a trust where you are the trustee?`
export const RESIDENT_VERIFIED_LABEL = `Can you verify that you are a resident at this property address?`
export const CONCERT_SUBMIT_FAILED_MESSAGE = `Failed to submit the application. Correct any highlighted details and try again.`
export const PRICING_REFRESHED_MESSAGE = `Pricing refreshed. Review the terms on the proposal, then submit again.`
export const HOMEOWNER_EMAIL_REQUIRED_MESSAGE = `Concert requires the homeowner's email address for ESIGN consent. Add it to the project in PowurOS before applying.`

/** Homeowner and Site facts the form opens with; the server confirmed them. */
export type ConcertApplicantPrefill = {
  firstName: string
  lastName: string
  email: string
  phone: string
  street: string
  city: string
  state: string
  zip: string
}

type ConcertFormData = {
  phoneNumber: string
  ssn: string
  dob: string
  grossAnnualIncome: string
  ownerVerified: boolean
  residentVerified: boolean
  yearsAtResidence: string
  monthlyMortgage: string
  currentOccupation: string
  lengthOfEmployment: string
  showCoApplicant: boolean
  coFirstName: string
  coMiddleInitial: string
  coLastName: string
  coEmail: string
  coPhoneNumber: string
  coAddress1: string
  coAddress2: string
  coCity: string
  coState: string
  coZip: string
  coMailAddress1: string
  coMailAddress2: string
  coMailCity: string
  coMailState: string
  coMailZip: string
  coSsn: string
  coDob: string
  coGrossAnnualIncome: string
  coYearsAtResidence: string
  coMonthlyMortgage: string
  coCurrentOccupation: string
  coLengthOfEmployment: string
}

type TextFieldKey = Exclude<
  keyof ConcertFormData,
  `ownerVerified` | `residentVerified` | `showCoApplicant`
>

const DIGIT_ONLY_FIELDS = new Set<TextFieldKey>([
  `lengthOfEmployment`,
  `yearsAtResidence`,
  `monthlyMortgage`,
  `coLengthOfEmployment`,
  `coYearsAtResidence`,
  `coMonthlyMortgage`,
])

const CO_APPLICANT_REQUIRED: readonly TextFieldKey[] = [
  `coFirstName`,
  `coLastName`,
  `coAddress1`,
  `coCity`,
  `coState`,
  `coZip`,
  `coMailAddress1`,
  `coMailCity`,
  `coMailState`,
  `coMailZip`,
  `coEmail`,
  `coYearsAtResidence`,
  `coMonthlyMortgage`,
  `coGrossAnnualIncome`,
  `coCurrentOccupation`,
  `coLengthOfEmployment`,
]

const normalizeDigits = (value: string): string => value.replace(/\D/g, ``)

const formatSsnForConcert = (value: string): string => {
  const digits = normalizeDigits(value)
  return digits.length === 9
    ? `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
    : digits
}

const normalizeStreetAddress = (value: string): string =>
  value.split(`,`)[0]?.trim() ?? ``

const parseIncome = (value: string): string => {
  const plain = value.replace(/,/g, ``).trim()
  if (!/^\d+(\.\d+)?$/.test(plain)) {
    return ``
  }
  const numeric = Number(plain)
  return numeric <= 0 ? `` : String(numeric)
}

const isValidSsn = (value: string): boolean =>
  normalizeDigits(value).length === 9
const isPresent = (value: string): boolean => value.trim().length > 0

const initialFormData = (prefill: ConcertApplicantPrefill): ConcertFormData => ({
  phoneNumber: prefill.phone,
  ssn: ``,
  dob: ``,
  grossAnnualIncome: ``,
  ownerVerified: false,
  residentVerified: false,
  yearsAtResidence: ``,
  monthlyMortgage: ``,
  currentOccupation: ``,
  lengthOfEmployment: ``,
  showCoApplicant: false,
  coFirstName: ``,
  coMiddleInitial: ``,
  coLastName: ``,
  coEmail: ``,
  coPhoneNumber: ``,
  coAddress1: prefill.street,
  coAddress2: ``,
  coCity: prefill.city,
  coState: prefill.state,
  coZip: prefill.zip,
  coMailAddress1: prefill.street,
  coMailAddress2: ``,
  coMailCity: prefill.city,
  coMailState: prefill.state,
  coMailZip: prefill.zip,
  coSsn: ``,
  coDob: ``,
  coGrossAnnualIncome: ``,
  coYearsAtResidence: ``,
  coMonthlyMortgage: ``,
  coCurrentOccupation: ``,
  coLengthOfEmployment: ``,
})

function Group({
  title,
  children,
}: {
  title: string
  children: ReactNode
}): JSX.Element {
  return (
    <fieldset className="flex flex-col gap-4 rounded-lg border border-bp-border p-4">
      <legend className="px-1 text-[length:var(--step--1)] font-semibold text-bp-text">
        {title}
      </legend>
      {children}
    </fieldset>
  )
}

function Row({ children }: { children: ReactNode }): JSX.Element {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>
}

function Checkbox({
  checked,
  onChange,
  children,
  testId,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  children: ReactNode
  testId?: string
}): JSX.Element {
  return (
    <label className="flex items-start gap-3 text-[length:var(--step--2)] text-bp-text">
      <input
        type="checkbox"
        checked={checked}
        data-testid={testId}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 rounded border-bp-border-strong"
      />
      <span>{children}</span>
    </label>
  )
}

/**
 * Concert's in-person application for any proposal kind: V1's fields,
 * validation, disclosures, consent and optional co-applicant. The customer
 * identity was confirmed by Initiate and the server supplies the installation
 * address and financed amount. Opening or closing this drawer never runs
 * credit.
 */
export function ConcertApplicationForm({
  bundle,
  proposalId,
  quoteId,
  prefill,
  onClose,
  onSubmitted,
}: {
  bundle: ConcertApplicationBundle
  proposalId: string
  quoteId: string
  prefill: ConcertApplicantPrefill
  onClose: () => void
  /** Null when the server reports an Existing Loan Application already. */
  onSubmitted: (status: ConcertApplicationStatusView | null) => void
}): JSX.Element {
  const submitApplication = bundle.useSubmit()
  const { RefreshPricing } = bundle
  // One key per opened form: a retry of the same attempt reuses it, so the
  // server can never turn a double click into two credit applications.
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const [formData, setFormData] = useState(() => initialFormData(prefill))
  const [disclosureAccepted, setDisclosureAccepted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [ssnError, setSsnError] = useState<string | undefined>(undefined)
  const [coSsnError, setCoSsnError] = useState<string | undefined>(undefined)
  const [refreshRequired, setRefreshRequired] = useState(false)
  const [pricingRefreshed, setPricingRefreshed] = useState(false)

  const updateField = (field: TextFieldKey, value: string): void => {
    setFormData((previous) => ({
      ...previous,
      [field]: DIGIT_ONLY_FIELDS.has(field) ? normalizeDigits(value) : value,
    }))
    setSubmitError(null)
    if (field === `ssn`) setSsnError(undefined)
    if (field === `coSsn`) setCoSsnError(undefined)
  }

  const setFlag = (
    field: `ownerVerified` | `residentVerified`,
    checked: boolean,
  ): void => {
    setFormData((previous) => ({ ...previous, [field]: checked }))
    setSubmitError(null)
  }

  const toggleCoApplicant = (show: boolean): void => {
    setFormData((previous) => ({
      ...initialFormData(prefill),
      ...(show
        ? {
            ...previous,
            coAddress1: previous.coAddress1 || prefill.street,
            coCity: previous.coCity || prefill.city,
            coState: previous.coState || prefill.state,
            coZip: previous.coZip || prefill.zip,
            coMailAddress1: previous.coMailAddress1 || prefill.street,
            coMailCity: previous.coMailCity || prefill.city,
            coMailState: previous.coMailState || prefill.state,
            coMailZip: previous.coMailZip || prefill.zip,
          }
        : {
            phoneNumber: previous.phoneNumber,
            ssn: previous.ssn,
            dob: previous.dob,
            grossAnnualIncome: previous.grossAnnualIncome,
            ownerVerified: previous.ownerVerified,
            residentVerified: previous.residentVerified,
            yearsAtResidence: previous.yearsAtResidence,
            monthlyMortgage: previous.monthlyMortgage,
            currentOccupation: previous.currentOccupation,
            lengthOfEmployment: previous.lengthOfEmployment,
          }),
      showCoApplicant: show,
    }))
    setCoSsnError(undefined)
    setSubmitError(null)
  }

  const isFormValid = useMemo(() => {
    const required = [
      formData.ssn,
      formData.dob,
      formData.grossAnnualIncome,
      formData.yearsAtResidence,
      formData.monthlyMortgage,
      formData.currentOccupation,
      formData.lengthOfEmployment,
    ]
    const coApplicantValid =
      !formData.showCoApplicant ||
      (CO_APPLICANT_REQUIRED.every((key) => isPresent(formData[key])) &&
        isValidSsn(formData.coSsn) &&
        isPresent(formData.coDob) &&
        parseIncome(formData.coGrossAnnualIncome).length > 0)
    return (
      isPresent(prefill.email) &&
      required.every(isPresent) &&
      isValidSsn(formData.ssn) &&
      parseIncome(formData.grossAnnualIncome).length > 0 &&
      formData.ownerVerified &&
      formData.residentVerified &&
      disclosureAccepted &&
      coApplicantValid
    )
  }, [disclosureAccepted, formData, prefill.email])

  const handleSubmit = (event?: FormEvent): void => {
    event?.preventDefault()
    setSubmitError(null)

    if (!isValidSsn(formData.ssn)) {
      setSsnError(`SSN must be 9 digits`)
      setSubmitError(`Please correct the SSN before submitting.`)
      return
    }
    if (!isPresent(formData.dob)) {
      setSubmitError(`Date of birth is required.`)
      return
    }
    if (formData.showCoApplicant) {
      if (CO_APPLICANT_REQUIRED.some((key) => !isPresent(formData[key]))) {
        setSubmitError(`Please complete all required co-applicant fields.`)
        return
      }
      if (!isValidSsn(formData.coSsn)) {
        setCoSsnError(`SSN must be 9 digits`)
        setSubmitError(`Please correct the co-applicant SSN before submitting.`)
        return
      }
      if (!isPresent(formData.coDob)) {
        setSubmitError(`Co-applicant date of birth is required.`)
        return
      }
    }
    if (!formData.ownerVerified || !formData.residentVerified) {
      setSubmitError(`Please confirm ownership and residency before submitting.`)
      return
    }
    const normalizedIncome = parseIncome(formData.grossAnnualIncome)
    if (!normalizedIncome) {
      setSubmitError(`Gross annual income must be greater than 0.`)
      return
    }
    const normalizedCoIncome = formData.showCoApplicant
      ? parseIncome(formData.coGrossAnnualIncome)
      : ``
    if (formData.showCoApplicant && !normalizedCoIncome) {
      setSubmitError(`Co-applicant gross annual income must be greater than 0.`)
      return
    }
    if (!disclosureAccepted) {
      setSubmitError(
        `You must accept all disclosures and agreements before submitting.`,
      )
      return
    }

    const street = normalizeStreetAddress(prefill.street)
    const coApplicant: ConcertSubmitApplicationCoApplicantDto | undefined =
      formData.showCoApplicant
        ? {
            csecondaryfirstname: formData.coFirstName.trim(),
            csecondarymiddleinitial:
              formData.coMiddleInitial.trim() || undefined,
            csecondarylastname: formData.coLastName.trim(),
            csecondaryaddress1: normalizeStreetAddress(formData.coAddress1),
            csecondaryaddress2: formData.coAddress2.trim() || undefined,
            csecondarycity: formData.coCity.trim(),
            csecondarystate: formData.coState.trim(),
            csecondaryzip: formData.coZip.trim(),
            csecondaryemail: formData.coEmail.trim(),
            csecondarymonthlymortgage: formData.coMonthlyMortgage.trim(),
            csecondaryyearsatresidence: formData.coYearsAtResidence.trim(),
            csecondaryaddress1_mail: normalizeStreetAddress(
              formData.coMailAddress1,
            ),
            csecondaryaddress2_mail: formData.coMailAddress2.trim() || undefined,
            csecondarycity_mail: formData.coMailCity.trim(),
            csecondarystate_mail: formData.coMailState.trim(),
            csecondaryzip_mail: formData.coMailZip.trim(),
            csecondarycurrentoccupation: formData.coCurrentOccupation.trim(),
            csecondarygrossannualincome: normalizedCoIncome,
            csecondarydob: formData.coDob,
            csecondaryterms1: `agreed`,
            csecondaryterms2: `agreed`,
            csecondarylengthofemployment: formData.coLengthOfEmployment.trim(),
            csecondaryphone: normalizeDigits(formData.coPhoneNumber),
            csecondaryssn: formatSsnForConcert(formData.coSsn),
            coapplicantSignature: formData.coEmail.trim(),
          }
        : undefined

    const body: ConcertSubmitApplicationBody = {
      quoteId,
      idempotencyKey,
      customer: {
        cfirstname: prefill.firstName.trim(),
        clastname: prefill.lastName.trim(),
        caddress1: street,
        caddress1_mail: street,
        ccity: prefill.city.trim(),
        ccity_mail: prefill.city.trim(),
        cstate: prefill.state.trim(),
        cstate_mail: prefill.state.trim(),
        czip: prefill.zip.trim(),
        czip_mail: prefill.zip.trim(),
        cemail: prefill.email.trim() || undefined,
        cphone1: normalizeDigits(formData.phoneNumber) || undefined,
      },
      application: {
        applicantSignature: prefill.email.trim(),
        cssn: formatSsnForConcert(formData.ssn),
        cdob: formData.dob,
        cgrossannualincome: normalizedIncome,
        cresidencetype: `OWN`,
        cowner: String(formData.ownerVerified),
        cresident: String(formData.residentVerified),
        cyearsatresidence: formData.yearsAtResidence.trim(),
        cmonthlymortgage: formData.monthlyMortgage.trim(),
        ccurrentoccupation: formData.currentOccupation.trim(),
        clengthofemployment: formData.lengthOfEmployment.trim(),
        cterms1: `agreed`,
        cterms2: `agreed`,
      },
      ...(coApplicant ? { coApplicant } : {}),
    }

    setRefreshRequired(false)
    setPricingRefreshed(false)
    submitApplication.mutate(
      { proposalId, body },
      {
        onSuccess: (status) => {
          onSubmitted(status)
        },
        onError: (error) => {
          if (concertErrorCode(error) === `CONCERT_APPLICATION_EXISTS`) {
            onSubmitted(null)
            return
          }
          setRefreshRequired(bundle.isRefreshRequiredError(error))
          setSubmitError(bundle.describeSubmitError(error))
        },
      },
    )
  }

  const submitting = submitApplication.isLoading
  const field = (
    key: TextFieldKey,
    label: string,
    options: {
      type?: string
      required?: boolean
      placeholder?: string
      error?: string
    } = {},
  ): JSX.Element => (
    <TextField
      label={label}
      type={options.type ?? `text`}
      value={formData[key]}
      required={options.required}
      placeholder={options.placeholder}
      error={options.error}
      disabled={submitting}
      onChange={(event) => updateField(key, event.target.value)}
    />
  )

  return (
    <Drawer
      open
      theme="dark"
      // Escape and the backdrop must not drop an in-flight submission.
      onClose={submitting ? () => undefined : onClose}
      title={CONCERT_APPLICATION_TITLE}
      // The application is long and two-column; give it most of the viewport.
      widthClass="sm:max-w-[90vw]"
      hideCloseButton
      contentClassName="flex flex-col pb-0"
      data-testid={`${bundle.testIdPrefix}-concert-application-form`}
      header={({ close }) => (
        <div className="border-b border-bp-border bg-bp-base px-6 pt-5 pb-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Eyebrow className="mb-1">{CONCERT_EYEBROW}</Eyebrow>
              <h2 className="font-display text-[length:var(--step-2)] font-semibold text-bp-text">
                {CONCERT_APPLICATION_TITLE}
              </h2>
              <p className="mt-1 text-[length:var(--step--1)] text-bp-text-muted">
                In-person application for {prefill.firstName} {prefill.lastName}.
                Nothing is sent to Concert until you submit.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              data-testid={`${bundle.testIdPrefix}-concert-application-close`}
              className="rounded-md px-2 py-1 text-[length:var(--step--2)] font-semibold text-bp-text-muted hover:text-bp-text"
            >
              Close
            </button>
          </div>
        </div>
      )}
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-1 flex-col gap-5"
        noValidate
      >
        {submitError ? (
          <div
            role="alert"
            data-testid={`${bundle.testIdPrefix}-concert-submit-error`}
            className="flex flex-col items-start gap-2 rounded-md border border-bp-danger/40 bg-bp-danger/10 px-3 py-2 text-[length:var(--step--2)] text-bp-danger"
          >
            <span>{submitError}</span>
            {refreshRequired ? (
              <RefreshPricing
                proposalId={proposalId}
                onRefreshed={() => {
                  setRefreshRequired(false)
                  setPricingRefreshed(true)
                  setSubmitError(null)
                }}
              />
            ) : null}
          </div>
        ) : null}
        {!isPresent(prefill.email) ? (
          <p
            role="alert"
            data-testid={`${bundle.testIdPrefix}-concert-email-required`}
            className="rounded-md border border-bp-danger/40 bg-bp-danger/10 px-3 py-2 text-[length:var(--step--2)] text-bp-danger"
          >
            {HOMEOWNER_EMAIL_REQUIRED_MESSAGE}
          </p>
        ) : null}
        {pricingRefreshed ? (
          <p
            role="status"
            className="text-[length:var(--step--2)] text-bp-success"
          >
            {PRICING_REFRESHED_MESSAGE}
          </p>
        ) : null}

        <Group title="Applicant">
          <Row>
            <TextField label="First name" value={prefill.firstName} disabled />
            <TextField label="Last name" value={prefill.lastName} disabled />
          </Row>
          <Row>
            <TextField
              label="Email address"
              type="email"
              value={prefill.email}
              disabled
            />
            {field(`phoneNumber`, `Phone number`, { type: `tel` })}
          </Row>
        </Group>

        <Group title="Installation address">
          <TextField label="Street address" value={prefill.street} disabled />
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField label="City" value={prefill.city} disabled />
            <TextField label="State" value={prefill.state} disabled />
            <TextField label="ZIP code" value={prefill.zip} disabled />
          </div>
        </Group>

        <Group title="Identity verification">
          <Row>
            {field(`ssn`, `Social Security number`, {
              type: `password`,
              required: true,
              placeholder: `9-digit SSN`,
              error: ssnError,
            })}
            {field(`dob`, `Date of birth`, { type: `date`, required: true })}
          </Row>
        </Group>

        <Group title="Financial and property information">
          <Row>
            {field(`yearsAtResidence`, `Years at residence`, {
              required: true,
            })}
            {field(`monthlyMortgage`, `Monthly mortgage`, { required: true })}
          </Row>
          <Row>
            {field(`currentOccupation`, `Current occupation`, {
              required: true,
            })}
            {field(`lengthOfEmployment`, `Length of employment in years`, {
              required: true,
            })}
          </Row>
          {field(`grossAnnualIncome`, `Gross annual income`, {
            required: true,
            placeholder: `e.g. 120000`,
          })}
          <div className="flex flex-col gap-3 rounded-md border border-bp-border p-3">
            <Checkbox
              checked={formData.ownerVerified}
              onChange={(checked) => setFlag(`ownerVerified`, checked)}
              testId={`${bundle.testIdPrefix}-concert-owner-verified`}
            >
              {OWNER_VERIFIED_LABEL}
            </Checkbox>
            <Checkbox
              checked={formData.residentVerified}
              onChange={(checked) => setFlag(`residentVerified`, checked)}
              testId={`${bundle.testIdPrefix}-concert-resident-verified`}
            >
              {RESIDENT_VERIFIED_LABEL}
            </Checkbox>
          </div>
        </Group>

        {formData.showCoApplicant ? (
          <Group title="Co-applicant">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => toggleCoApplicant(false)}
                className="rounded-md px-2 py-1 text-[length:var(--step--2)] font-semibold text-bp-danger hover:bg-bp-danger/10"
              >
                {REMOVE_CO_APPLICANT_LABEL}
              </button>
            </div>
            <Row>
              {field(`coFirstName`, `First name`, { required: true })}
              {field(`coLastName`, `Last name`, { required: true })}
            </Row>
            <Row>
              {field(`coMiddleInitial`, `Middle initial`)}
              {field(`coDob`, `Date of birth`, { type: `date`, required: true })}
            </Row>
            <Row>
              {field(`coEmail`, `Email address`, {
                type: `email`,
                required: true,
              })}
              {field(`coPhoneNumber`, `Phone number`, {
                type: `tel`,
                required: true,
              })}
            </Row>
            {field(`coSsn`, `Social Security number`, {
              type: `password`,
              required: true,
              placeholder: `9-digit SSN`,
              error: coSsnError,
            })}
            <Row>
              {field(`coYearsAtResidence`, `Years at residence`, {
                required: true,
              })}
              {field(`coMonthlyMortgage`, `Monthly mortgage`, {
                required: true,
              })}
            </Row>
            <Row>
              {field(`coCurrentOccupation`, `Current occupation`, {
                required: true,
              })}
              {field(`coLengthOfEmployment`, `Length of employment in years`, {
                required: true,
              })}
            </Row>
            {field(`coGrossAnnualIncome`, `Gross annual income`, {
              required: true,
              placeholder: `e.g. 100000`,
            })}
            <Row>
              {field(`coAddress1`, `Street address`, { required: true })}
              {field(`coAddress2`, `Address line 2`)}
            </Row>
            <div className="grid gap-4 sm:grid-cols-3">
              {field(`coCity`, `City`, { required: true })}
              {field(`coState`, `State`, { required: true })}
              {field(`coZip`, `ZIP code`, { required: true })}
            </div>
            <p className="text-[length:var(--step--2)] font-semibold tracking-widest text-bp-text-muted uppercase">
              Co-applicant mailing address
            </p>
            <Row>
              {field(`coMailAddress1`, `Mailing address`, { required: true })}
              {field(`coMailAddress2`, `Mailing address line 2`)}
            </Row>
            <div className="grid gap-4 sm:grid-cols-3">
              {field(`coMailCity`, `Mailing city`, { required: true })}
              {field(`coMailState`, `Mailing state`, { required: true })}
              {field(`coMailZip`, `Mailing ZIP`, { required: true })}
            </div>
          </Group>
        ) : (
          <button
            type="button"
            onClick={() => toggleCoApplicant(true)}
            className="self-center rounded-md border border-dashed border-bp-border-strong px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-text hover:border-bp-accent"
          >
            {ADD_CO_APPLICANT_LABEL}
          </button>
        )}

        <Group title="Disclosures and agreements">
          <div className="flex max-h-56 flex-col gap-3 overflow-y-auto pr-1 text-[length:var(--step--2)] text-bp-text-muted">
            <p>
              I, the above-named Applicant, confirm the following: I have entered
              the above information myself, including my valid email address, and
              all information I have provided is true and correct.
            </p>
            <div>
              <p className="font-semibold text-bp-text">
                PATRIOT ACT DISCLOSURE
              </p>
              <p>
                To help the government fight the funding of terrorism and money
                laundering activities, federal law requires all financial
                institutions to obtain, verify, and record information that
                identifies each person who opens an account.
              </p>
              <p>
                What this means for you: When you open an account, we will ask
                for your name, address, date of birth, and other information that
                will allow us to identify you. We may also ask to see your
                driver&apos;s license or other identifying documents.
              </p>
            </div>
            <div>
              <p className="font-semibold text-bp-text">ESIGN CONSENT</p>
              <p>
                By entering your email address below, you agree to the ESIGN
                consent that is outlined here.
              </p>
            </div>
            <div>
              <p className="font-semibold text-bp-text">READY TO APPLY</p>
              <p>
                I, the above-named Applicant, confirm my email address below, and
                I certify and agree that: (a) I am providing the information
                herein to TriBeam Financial, Inc. (&quot;Beam&quot;) in order to
                pre-qualify for financing; (b) the information provided is true,
                correct, and complete to the best of my knowledge; (c) by
                clicking &quot;Apply&quot;, I authorize Beam to verify and share
                the information provided in this application for
                pre-qualification and pull my credit report for the purposes of
                (i) determining eligibility for financing through one of its
                financing partners, (ii) the administration, review, or
                collection of your account, and (iii) receiving enhanced or
                additional products or services; (d) I am applying for financing
                on my own behalf and not on behalf of any other person; (e) I
                give Beam permission to share my application information and
                credit decision with my contractor, and (f) that I am a U.S.
                resident.
              </p>
              <p>
                California Residents: If married, you may apply for a separate
                account.
              </p>
              <p>
                I have read and agree to all consents and agreements. If applying
                with a co-applicant, I acknowledge my intent for applying
                jointly.
              </p>
            </div>
          </div>
          <Checkbox
            checked={disclosureAccepted}
            onChange={(checked) => {
              setDisclosureAccepted(checked)
              setSubmitError(null)
            }}
            testId={`${bundle.testIdPrefix}-concert-disclosure-accepted`}
          >
            {DISCLOSURE_ACCEPT_LABEL}
          </Checkbox>
        </Group>

        <footer className="sticky bottom-0 -mx-4 mt-auto flex items-center justify-between gap-3 border-t border-bp-border bg-bp-elevated px-4 py-4 sm:-mx-6 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-bp-border-strong px-4 py-2 text-[length:var(--step--1)] font-medium text-bp-text-soft hover:border-bp-accent hover:text-bp-text disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || submitting}
            className={clsx(
              `rounded-md px-5 py-2.5 text-[length:var(--step--1)] font-semibold`,
              isFormValid && !submitting
                ? `bg-bp-accent text-bp-on-accent hover:bg-bp-accent-hover`
                : `cursor-not-allowed border border-bp-border-strong bg-bp-elevated text-bp-text-dim`,
            )}
          >
            {submitting ? `Submitting application` : SUBMIT_APPLICATION_LABEL}
          </button>
        </footer>
      </form>
    </Drawer>
  )
}
